/**
 * Iter 69 — Stellar Contracts module (Owner-only).
 *
 * Single-page surface that handles:
 *   • List view with status filter pills.
 *   • New Contract flow (template picker → form → Save/Generate/Send).
 *   • Detail drawer (editable while draft, locked once sent, signed PDF upload).
 *
 * Access control:
 *   • Route guard (handled in App.js) redirects non-owners to /tasks.
 *   • Defensive in-page guard: shows a "Owner access only" notice if the user
 *     somehow lands here without owner role.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { contractsAPI } from '../lib/api';
import {
  FileText, Plus, Send, Download, Trash2, CheckCircle2, X,
  Pencil, Lock, Mail, ArrowLeft, Upload, Loader2, RotateCcw, Archive,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import ContractDatePicker from '../components/ContractDatePicker';
import { Card } from '../components/ui/card';

const TEMPLATE_LABELS = {
  media_partnership: 'Media Partnership Agreement',
  offer_letter: 'Job Offer Letter',
  employment_agreement: 'Employment Agreement',
  resignation_acceptance: 'Resignation Acceptance Letter',
  experience_letter: 'Experience Letter',
  termination_letter: 'Termination Letter',
};

const TEMPLATE_DESCRIPTIONS = {
  media_partnership: 'For external clients — scope of work, pricing, deliverables.',
  offer_letter: 'For new hires — position, compensation, probation, responsibilities.',
  employment_agreement: 'Full employment contract — duties, leave, notice, jurisdiction.',
  resignation_acceptance: 'Acknowledge resignation, confirm last working day, set handover expectations.',
  experience_letter: 'Service Certificate for current or former employees.',
  termination_letter: 'Company-initiated separation. Jurisdiction-aware language.',
};

// Iter 69.3 — group templates in the picker
const TEMPLATE_GROUPS = [
  { id: 'client', label: 'Client Contracts', templates: ['media_partnership'] },
  { id: 'hiring', label: 'Hiring', templates: ['offer_letter', 'employment_agreement'] },
  { id: 'exit',   label: 'Employee Exit', templates: ['resignation_acceptance', 'experience_letter', 'termination_letter'] },
];

const STATUS_STYLES = {
  draft:  'bg-muted text-muted-foreground border-border',
  sent:   'bg-amber-500/10 text-amber-500 border-amber-500/30',
  signed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      data-testid={`contract-status-badge-${status}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${cls}`}
    >
      {status}
    </span>
  );
}

// D71 — engagement-tracking badge. Goes alongside the lifecycle StatusBadge
// in lists and detail. `tracking` comes from the backend: it's a roll-up
// of `contract_email_events` for this contract.
const TRACK_STYLES = {
  sent:     'bg-slate-500/10 text-slate-300 border-slate-500/30',
  opened:   'bg-sky-500/10 text-sky-400 border-sky-500/30',
  viewed:   'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  bounced:  'bg-red-500/10 text-red-400 border-red-500/30',
  draft:    'bg-muted text-muted-foreground border-border',
};
const TRACK_LABELS = {
  sent: 'Sent', opened: 'Opened', viewed: 'Viewed',
  bounced: 'Bounced', draft: '—',
};

function TrackingBadge({ tracking }) {
  if (!tracking) return null;
  const label = tracking.status_label || 'draft';
  const cls = TRACK_STYLES[label] || TRACK_STYLES.draft;
  return (
    <span
      data-testid={`tracking-badge-${label}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
      title={
        label === 'opened' ? 'Email opened (approximate — Apple Mail proxy / image-block can skew this)' :
        label === 'viewed' ? 'Recipient clicked the View Contract link (strong engagement signal)' :
        label === 'bounced' ? (tracking.bounce_reason || 'Send failed — see detail') :
        ''
      }
    >
      {TRACK_LABELS[label] || label}
    </span>
  );
}

function FollowUpFlag({ tracking }) {
  if (!tracking?.needs_followup) return null;
  const reasonText = tracking.followup_reason === 'bounced'
    ? 'Bounced — verify recipient address'
    : 'No engagement in 3+ days';
  return (
    <span
      data-testid="contract-needs-followup"
      className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 px-2 py-0.5 text-[11px] font-medium animate-pulse"
      title={reasonText}
    >
      Needs follow-up
    </span>
  );
}

import { formatDateLong, formatDateTimeLong } from '../lib/dateFormat';
import { formatLocalDate, formatLocalDateTime } from '../utils/date';

// Phase 2 Group A — render contract timestamps in the viewer's local timezone.
// Server stores created_at/sent_at/signed_at as UTC ISO strings; toLocaleString
// in formatLocalDate* applies the user's TZ offset.
function fmtDate(iso) {
  if (!iso) return '—';
  return formatLocalDate(iso, { month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return formatLocalDateTime(iso, { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function ContractsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const wsId = currentWorkspace?.id;

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editing, setEditing] = useState(null); // contract object being edited/viewed
  const [options, setOptions] = useState(null);

  // Owner-only guard
  const isOwner = useMemo(
    () => user?.role === 'super_admin' || workspaceRole === 'owner',
    [user, workspaceRole],
  );

  useEffect(() => {
    if (!isOwner) return;
    contractsAPI.options().then(r => setOptions(r.data)).catch(() => {});
  }, [isOwner]);

  const [viewMode, setViewMode] = useState('active'); // 'active' | 'trash'

  const load = async () => {
    if (!wsId || !isOwner) return;
    setLoading(true);
    try {
      const r = await contractsAPI.list(wsId, { archived: viewMode === 'trash' ? 'true' : 'false' });
      setContracts(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, isOwner, viewMode]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return contracts;
    return contracts.filter(c => c.status === statusFilter);
  }, [contracts, statusFilter]);

  if (!isOwner) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="contracts-owner-only-notice">
        <Card className="p-8 text-center border-amber-500/30 bg-amber-500/5">
          <Lock className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2">Owner access only</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Contracts are restricted to workspace Owners. If you need access, ask Dhrumil.
          </p>
          <Button onClick={() => navigate('/tasks')} variant="secondary">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="contracts-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <FileText className="h-6 w-6 text-gold" /> Contracts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Owner-only · Generate, send, and track signed agreements.
          </p>
        </div>
        <Button
          data-testid="manage-templates-btn"
          onClick={() => navigate('/tasks/contracts/templates')}
          variant="outline"
          className="mr-2"
        >
          <FileText className="h-4 w-4 mr-2" /> Manage Templates
        </Button>
        <Button
          data-testid="new-contract-btn"
          onClick={() => setShowTemplatePicker(true)}
          className="bg-gold text-black hover:bg-gold/90 font-medium"
        >
          <Plus className="h-4 w-4 mr-2" /> New Contract
        </Button>
      </header>

      {/* D73 — Active / Trash toggle */}
      <div className="flex items-center gap-2 mb-3" data-testid="view-mode-toggle">
        <Button
          data-testid="view-mode-active"
          size="sm"
          variant={viewMode === 'active' ? 'default' : 'outline'}
          onClick={() => setViewMode('active')}
        >
          Active
        </Button>
        <Button
          data-testid="view-mode-trash"
          size="sm"
          variant={viewMode === 'trash' ? 'default' : 'outline'}
          onClick={() => setViewMode('trash')}
          className={viewMode === 'trash' ? 'bg-red-600 text-white hover:bg-red-700' : 'text-muted-foreground'}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Trash{contracts.length > 0 && viewMode === 'trash' ? ` (${contracts.length})` : ''}
        </Button>
      </div>

      {viewMode === 'trash' && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 mb-3">
          Archived contracts. Click <strong>Restore</strong> to bring back, or <strong>Delete forever</strong> to permanently remove (cascades the PDF + tracking events).
        </div>
      )}

      {/* Filter pills (active view only) */}
      {viewMode === 'active' && (
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-4">
          <TabsList data-testid="contracts-filter-tabs">
            <TabsTrigger value="all"    data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="draft"  data-testid="filter-draft">Draft</TabsTrigger>
            <TabsTrigger value="sent"   data-testid="filter-sent">Sent</TabsTrigger>
            <TabsTrigger value="signed" data-testid="filter-signed">Signed</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* List */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="contracts-loading">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
            Loading contracts…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="contracts-empty">
            <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              No contracts yet. Click + New Contract to create your first.
            </p>
            <Button
              data-testid="contracts-empty-cta"
              variant="secondary"
              onClick={() => setShowTemplatePicker(true)}
            >
              <Plus className="h-4 w-4 mr-2" /> New Contract
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="contracts-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-3 font-medium">Template</th>
                <th className="text-left px-4 py-3 font-medium">Recipient</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Engagement</th>
                <th className="text-left px-4 py-3 font-medium">Created</th>
                <th className="text-left px-4 py-3 font-medium">Sent</th>
                <th className="text-left px-4 py-3 font-medium">Signed</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr
                  key={c.id}
                  className="border-b border-border hover:bg-muted/40 cursor-pointer transition-colors"
                  data-testid={`contract-row-${c.id}`}
                  onClick={() => setEditing(c)}
                >
                  <td className="px-4 py-3 font-medium">{TEMPLATE_LABELS[c.template_type] || c.template_type}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.recipient_name || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <TrackingBadge tracking={c.tracking} />
                      <FollowUpFlag tracking={c.tracking} />
                      {c.tracking?.resend_count > 0 && (
                        <span
                          data-testid={`resend-count-${c.id}`}
                          className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                          title={`${c.tracking.resend_count} reminder${c.tracking.resend_count === 1 ? '' : 's'} sent`}
                        >
                          ↻ {c.tracking.resend_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(c.sent_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(c.signed_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {viewMode === 'trash' ? (
                      <TrashRowActions
                        wsId={wsId}
                        contract={c}
                        onChange={load}
                      />
                    ) : (
                      /* D73 — Owner-only archive button on each Active row.
                         Hidden entirely for managers/admins/members (the
                         page is owner-only anyway, but keep the guard for
                         defense-in-depth). */
                      <ArchiveRowAction
                        wsId={wsId}
                        contract={c}
                        isOwner={isOwner}
                        onChange={load}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Template picker modal */}
      <Dialog open={showTemplatePicker} onOpenChange={setShowTemplatePicker}>
        <DialogContent className="max-w-lg" data-testid="template-picker-modal">
          <DialogHeader>
            <DialogTitle>Choose a contract template</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {TEMPLATE_GROUPS.map(group => (
              <div key={group.id} data-testid={`tpl-group-${group.id}`}>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 font-semibold">
                  {group.label}
                </div>
                <div className="space-y-2">
                  {group.templates.map(key => (
                    <button
                      key={key}
                      data-testid={`template-pick-${key}`}
                      onClick={async () => {
                        try {
                          const r = await contractsAPI.create(wsId, {
                            template_type: key,
                            recipient_name: '',
                            recipient_email: '',
                            template_variables: {},
                          });
                          setShowTemplatePicker(false);
                          setEditing(r.data);
                          await load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to create contract');
                        }
                      }}
                      className="w-full text-left p-3 rounded-lg border border-border hover:border-gold/60 hover:bg-gold/5 transition-colors"
                    >
                      <div className="font-medium text-sm">{TEMPLATE_LABELS[key]}</div>
                      <div className="text-xs text-muted-foreground mt-1">{TEMPLATE_DESCRIPTIONS[key]}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail / Edit drawer */}
      {editing && (
        <ContractDetailModal
          contract={editing}
          options={options}
          wsId={wsId}
          onClose={() => setEditing(null)}
          onChanged={async (next) => {
            setEditing(next);
            await load();
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// D73 — Active-view row archive button. Owner-only. Hidden chevron when
// the caller is not the owner (defense-in-depth — the whole page is
// already gated, but we still hide the action so a stray render in a
// future shared sub-view never leaks it).
// =====================================================================
function ArchiveRowAction({ wsId, contract, isOwner, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!isOwner) {
    // Non-owners never see contracts here, but keep a neutral chevron
    // just in case the row table is ever rendered in a debug context.
    return <span className="text-muted-foreground" data-testid={`contract-row-chevron-${contract.id}`}>›</span>;
  }
  const archive = async (e) => {
    e.stopPropagation();
    const label = contract.recipient_name || TEMPLATE_LABELS[contract.template_type] || 'this contract';
    if (!window.confirm(`Move "${label}" to Trash? You can restore it from the Trash view.`)) return;
    setBusy(true);
    try {
      await contractsAPI.archive(wsId, contract.id);
      toast.success('Contract moved to Trash');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Move to Trash failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={archive}
      disabled={busy}
      className="p-1.5 rounded hover:bg-foreground/[0.06] text-muted-foreground/60 hover:text-destructive transition-colors"
      title="Move to Trash"
      data-testid={`contract-row-archive-btn-${contract.id}`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
    </button>
  );
}

// =====================================================================
// D73 — Trash view row actions (Restore + Delete forever)
// =====================================================================
function TrashRowActions({ wsId, contract, onChange }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const restore = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await contractsAPI.restore(wsId, contract.id);
      toast.success('Contract restored');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
      <Button
        size="sm"
        variant="outline"
        onClick={restore}
        disabled={busy}
        data-testid={`contract-restore-btn-${contract.id}`}
        title="Restore to active"
      >
        <RotateCcw className="h-3.5 w-3.5 mr-1" />
        Restore
      </Button>
      <Button
        size="sm"
        variant="destructive"
        onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
        disabled={busy}
        className="bg-red-700 hover:bg-red-600 text-white"
        data-testid={`contract-permanent-delete-btn-${contract.id}`}
        title="Delete forever"
      >
        <Trash2 className="h-3.5 w-3.5 mr-1" />
        Delete forever
      </Button>
      {confirmOpen && (
        <PermanentDeleteConfirmModal
          wsId={wsId}
          contract={contract}
          onClose={() => setConfirmOpen(false)}
          onDeleted={() => { setConfirmOpen(false); onChange?.(); }}
        />
      )}
    </div>
  );
}

// =====================================================================
// D73 — Permanent delete confirmation. Owner must type the recipient
// name (or template label fallback) exactly to enable the destructive
// action. This guards against muscle-memory clicks on an irreversible
// operation that also cascade-deletes the PDF blob + tracking events.
// =====================================================================
function PermanentDeleteConfirmModal({ wsId, contract, onClose, onDeleted }) {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const required = (contract.recipient_name || TEMPLATE_LABELS[contract.template_type] || 'DELETE').trim();
  const matches = phrase.trim() === required;

  const doDelete = async () => {
    setBusy(true);
    try {
      await contractsAPI.permanentDelete(wsId, contract.id);
      toast.success('Contract permanently deleted');
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Permanent delete failed');
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="contract-permanent-delete-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-300">
            <Trash2 className="h-5 w-5" />
            Delete contract forever
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1 text-sm">
          <div className="rounded-md border border-red-700/40 bg-red-950/30 p-3 text-[12px] text-red-200 leading-relaxed">
            This permanently removes the contract row, its PDF file in storage,
            and all email-tracking events (opens/views). <strong>This cannot be undone.</strong>
          </div>
          <div>
            <Label className="text-[12px] mb-1.5 block">
              Type <code className="bg-foreground/[0.08] px-1.5 py-0.5 rounded text-red-300">{required}</code> to confirm:
            </Label>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={required}
              autoFocus
              data-testid="contract-permanent-delete-phrase-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}
            data-testid="contract-permanent-delete-cancel-btn">
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!matches || busy}
            onClick={doDelete}
            className="bg-red-700 hover:bg-red-600 text-white disabled:bg-red-900/40 disabled:text-red-300/40"
            data-testid="contract-permanent-delete-confirm-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
            Delete forever
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =====================================================================
// Detail modal — handles edit / generate / send / mark-signed
// =====================================================================
function ContractDetailModal({ contract: initialContract, options, wsId, onClose, onChanged }) {  // D71 — Fetch full detail (including `email_events` timeline) on mount.
  // The list endpoint returns `tracking` summary but NOT the event array.
  const [contract, setContract] = useState(initialContract);
  useEffect(() => {
    let alive = true;
    contractsAPI.get(wsId, initialContract.id).then(r => {
      if (alive) setContract(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [wsId, initialContract.id]);

  const [form, setForm] = useState(() => ({
    recipient_name:  contract.recipient_name || '',
    recipient_email: contract.recipient_email || '',
    recipient_phone: contract.recipient_phone || '',
    template_variables: contract.template_variables || {},
  }));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSignedUpload, setShowSignedUpload] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [ccInput, setCcInput] = useState('');

  const isDraft = contract.status === 'draft';
  const isSent  = contract.status === 'sent';
  const isSigned = contract.status === 'signed';
  const isArchived = !!contract.archived_at;
  const [showPermDelete, setShowPermDelete] = useState(false);

  const setVar = (key, val) => {
    setForm(f => ({ ...f, template_variables: { ...f.template_variables, [key]: val } }));
  };
  const v = form.template_variables;

  const save = async () => {
    setSaving(true);
    try {
      const r = await contractsAPI.update(wsId, contract.id, form);
      toast.success('Draft saved');
      onChanged(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const generatePdf = async () => {
    setBusy(true);
    try {
      // Ensure latest form is persisted first
      const r1 = await contractsAPI.update(wsId, contract.id, form);
      await contractsAPI.generatePdf(wsId, contract.id);
      toast.success('PDF generated');
      onChanged({ ...r1.data, generated_pdf_url: `contracts/${contract.id}/generated.pdf` });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF generation failed');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!form.recipient_email) {
      toast.error('Recipient email is required before sending');
      return;
    }
    // Open the confirm modal so the user can add optional CCs.
    setShowSendConfirm(true);
  };

  const performSend = async () => {
    setBusy(true);
    try {
      const ccs = ccInput
        .split(',').map(e => e.trim()).filter(e => e && e.includes('@'));
      await contractsAPI.update(wsId, contract.id, form);
      const sendRes = await contractsAPI.send(wsId, contract.id, { cc_emails: ccs });
      const ccLabel = ccs.length ? ` + ${ccs.length} additional CC` : '';
      // D69 — surface fallback-sender usage and the actual sender address.
      const senderAddr = sendRes?.data?.sent_via_email || 'dhrumil@stellarproductions.ca';
      const usedFallback = !!sendRes?.data?.used_fallback;
      if (usedFallback) {
        toast.warning(
          `Sent to ${form.recipient_email} via FALLBACK sender (${senderAddr}). ` +
          `Primary sender was blocklisted by Hostinger — restore it in hPanel.`,
          { duration: 9000 }
        );
      } else {
        toast.success(`Contract sent to ${form.recipient_email} from ${senderAddr} (CC to you${ccLabel})`);
      }
      const r = await contractsAPI.get(wsId, contract.id);
      onChanged(r.data);
      setShowSendConfirm(false);
      setCcInput('');
    } catch (e) {
      // D69 — the backend now returns precise messages for SMTP blocklist /
      // disabled / unconfigured cases. Detect the blocklist class and prepend
      // a clear lead-in even if the upstream string is verbose.
      const raw = e.response?.data?.detail || e.message || 'Send failed';
      const lc = String(raw).toLowerCase();
      let msg = raw;
      if (lc.includes('blocklisted') && lc.includes('fallback')) {
        msg = `Sender address is blocklisted or disabled — fallback also unavailable. ${raw}`;
      } else if (lc.includes('5.7.1') || lc.includes('disabled by user')
                 || lc.includes('blocklist') || lc.includes('554')) {
        msg = `Sender address is blocklisted or disabled by SMTP provider. ${raw}`;
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    // D73 — DELETE now soft-archives (moves to Trash) regardless of status.
    const verb = isDraft ? 'this draft' : 'this contract';
    if (!window.confirm(`Move ${verb} to Trash? You can restore it from the Trash view.`)) return;
    setBusy(true);
    try {
      await contractsAPI.archive(wsId, contract.id);
      toast.success(isDraft ? 'Draft moved to Trash' : 'Contract moved to Trash');
      onChanged(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move to Trash failed');
      setBusy(false);
    }
  };

  // D73 — Restore an archived contract back to the active list.
  const restoreFromTrash = async () => {
    setBusy(true);
    try {
      await contractsAPI.restore(wsId, contract.id);
      toast.success('Contract restored');
      onChanged(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
      setBusy(false);
    }
  };

  const downloadPdf = (type = 'original') => {
    const url = contractsAPI.pdfUrl(wsId, contract.id, type);
    const token = localStorage.getItem('stellar_token');
    // Open with auth header is awkward in browser; use fetch + blob:
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch(() => toast.error('Failed to fetch PDF'));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="contract-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <FileText className="h-5 w-5 text-gold" />
            {TEMPLATE_LABELS[contract.template_type]}
            <StatusBadge status={contract.status} />
            {contract.tracking && (
              <>
                <TrackingBadge tracking={contract.tracking} />
                <FollowUpFlag tracking={contract.tracking} />
              </>
            )}
            {!isDraft && <Lock className="h-3.5 w-3.5 text-muted-foreground" title="Locked after sending" />}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Common recipient fields */}
          <section className="grid grid-cols-2 gap-3">
            <div>
              <Label>Recipient Name {contract.template_type === 'media_partnership' && '(Legal entity)'}</Label>
              <Input
                data-testid="field-recipient-name"
                disabled={!isDraft}
                value={form.recipient_name}
                onChange={e => setForm({ ...form, recipient_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Recipient Email</Label>
              <Input
                data-testid="field-recipient-email"
                type="email"
                disabled={!isDraft}
                value={form.recipient_email}
                onChange={e => setForm({ ...form, recipient_email: e.target.value })}
              />
            </div>
            <div>
              <Label>Recipient Phone (optional)</Label>
              <Input
                data-testid="field-recipient-phone"
                disabled={!isDraft}
                value={form.recipient_phone}
                onChange={e => setForm({ ...form, recipient_phone: e.target.value })}
              />
            </div>
          </section>

          {/* Template-specific */}
          {contract.template_type === 'media_partnership' && (
            <MediaPartnershipFields v={v} setVar={setVar} options={options} disabled={!isDraft} />
          )}
          {contract.template_type === 'offer_letter' && (
            <OfferLetterFields v={v} setVar={setVar} options={options} disabled={!isDraft} />
          )}
          {contract.template_type === 'employment_agreement' && (
            <EmploymentAgreementFields v={v} setVar={setVar} options={options} disabled={!isDraft} />
          )}
          {contract.template_type === 'resignation_acceptance' && (
            <ResignationAcceptanceFields v={v} setVar={setVar} disabled={!isDraft} />
          )}
          {contract.template_type === 'experience_letter' && (
            <ExperienceLetterFields v={v} setVar={setVar} disabled={!isDraft} />
          )}
          {contract.template_type === 'termination_letter' && (
            <TerminationLetterFields v={v} setVar={setVar} disabled={!isDraft} />
          )}

          {/* PDFs */}
          {(contract.generated_pdf_url || contract.signed_pdf_url) && (
            <section className="border-t border-border pt-4">
              <h3 className="text-sm font-medium mb-2">PDFs</h3>
              <div className="flex gap-2 flex-wrap">
                {contract.generated_pdf_url && (
                  <Button
                    data-testid="download-original-pdf-btn"
                    variant="secondary" size="sm"
                    onClick={() => downloadPdf('original')}
                  >
                    <Download className="h-4 w-4 mr-1.5" /> Original PDF
                  </Button>
                )}
                {contract.signed_pdf_url && (
                  <Button
                    data-testid="download-signed-pdf-btn"
                    variant="secondary" size="sm"
                    onClick={() => downloadPdf('signed')}
                  >
                    <Download className="h-4 w-4 mr-1.5" /> Signed PDF
                  </Button>
                )}
              </div>
            </section>
          )}

          {/* D71 — Engagement tracking (sent / signed only) */}
          {contract.tracking && (isSent || isSigned) && (
            <ContractTrackingSection
              tracking={contract.tracking}
              events={contract.email_events || []}
              isOwner={true}
              onResend={async (note) => {
                setBusy(true);
                try {
                  const res = await contractsAPI.resendReminder(wsId, contract.id, { note });
                  toast.success(
                    `Reminder sent · ${res.data?.sender_used === 'primary' ? 'dhrumil@' : 'crm@'}`,
                  );
                  const r = await contractsAPI.get(wsId, contract.id);
                  setContract(r.data);
                  onChanged(r.data);
                } catch (e) {
                  const raw = e.response?.data?.detail || 'Resend failed';
                  toast.error(raw);
                } finally {
                  setBusy(false);
                }
              }}
              busy={busy}
            />
          )}

          {/* Audit info */}
          <section className="border-t border-border pt-4 text-xs text-muted-foreground space-y-1">
            <div>Created: {fmtDate(contract.created_at)}</div>
            {contract.sent_at && <div>Sent: {fmtDate(contract.sent_at)}</div>}
            {contract.signed_at && <div>Signed: {fmtDate(contract.signed_at)}</div>}
          </section>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {isArchived ? (
            <>
              <div className="flex-1 text-xs text-amber-300/80 flex items-center gap-2">
                <Trash2 className="h-3.5 w-3.5" />
                In Trash — restore to reactivate or delete forever.
              </div>
              <Button
                data-testid="contract-detail-restore-btn"
                variant="outline"
                onClick={restoreFromTrash}
                disabled={busy}
              >
                <RotateCcw className="h-4 w-4 mr-1.5" /> Restore
              </Button>
              <Button
                data-testid="contract-detail-permanent-delete-btn"
                variant="destructive"
                onClick={() => setShowPermDelete(true)}
                disabled={busy}
                className="bg-red-700 hover:bg-red-600 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete forever
              </Button>
            </>
          ) : (
            <>
              {isDraft && (
                <>
                  <Button data-testid="delete-draft-btn" variant="ghost" onClick={remove} disabled={busy}>
                    <Trash2 className="h-4 w-4 mr-1.5" /> Move to Trash
                  </Button>
                  <div className="flex-1" />
                  <Button data-testid="save-draft-btn" variant="secondary" onClick={save} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Pencil className="h-4 w-4 mr-1.5" />}
                    Save as Draft
                  </Button>
                  <Button data-testid="generate-pdf-btn" variant="secondary" onClick={generatePdf} disabled={busy}>
                    {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
                    Generate PDF
                  </Button>
                  <Button
                    data-testid="send-contract-btn"
                    onClick={send} disabled={busy}
                    className="bg-gold text-black hover:bg-gold/90"
                  >
                    <Send className="h-4 w-4 mr-1.5" /> Send to Recipient
                  </Button>
                </>
              )}
              {isSent && (
                <>
                  <Button
                    data-testid="archive-contract-btn"
                    variant="ghost"
                    onClick={remove}
                    disabled={busy}
                    className="text-muted-foreground"
                  >
                    <Archive className="h-4 w-4 mr-1.5" /> Archive
                  </Button>
                  <div className="flex-1" />
                  <Button
                    data-testid="mark-signed-btn"
                    onClick={() => setShowSignedUpload(true)}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark as Signed
                  </Button>
                </>
              )}
              {isSigned && (
                <>
                  <Button
                    data-testid="archive-contract-btn"
                    variant="ghost"
                    onClick={remove}
                    disabled={busy}
                    className="text-muted-foreground"
                  >
                    <Archive className="h-4 w-4 mr-1.5" /> Archive
                  </Button>
                  <div className="flex-1 text-sm text-emerald-500 flex items-center gap-2 justify-end">
                    <CheckCircle2 className="h-4 w-4" />
                    Contract complete — both PDFs available above.
                  </div>
                </>
              )}
            </>
          )}
        </DialogFooter>

        {showPermDelete && (
          <PermanentDeleteConfirmModal
            wsId={wsId}
            contract={contract}
            onClose={() => setShowPermDelete(false)}
            onDeleted={() => { setShowPermDelete(false); onChanged(null); onClose(); }}
          />
        )}

        {/* Signed PDF upload sub-modal */}
        {showSignedUpload && (
          <SignedPdfUploadModal
            wsId={wsId}
            contractId={contract.id}
            onClose={() => setShowSignedUpload(false)}
            onUploaded={async () => {
              setShowSignedUpload(false);
              const r = await contractsAPI.get(wsId, contract.id);
              onChanged(r.data);
            }}
          />
        )}

        {/* Send confirmation modal with optional extra CCs (Iter 69.1 follow-up) */}
        {showSendConfirm && (
          <Dialog open onOpenChange={(o) => !o && !busy && setShowSendConfirm(false)}>
            <DialogContent className="max-w-md" data-testid="send-confirm-modal">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-gold" />
                  Send to recipient
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">To:</span>{' '}
                  <span className="font-medium" data-testid="send-confirm-recipient">{form.recipient_email}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">CC:</span>{' '}
                  <span className="font-medium">dhrumil@stellarproductions.ca</span>
                  <span className="text-xs text-muted-foreground ml-1">(always, for your records)</span>
                </div>
                <div>
                  <Label htmlFor="cc-extra">Additional CCs (optional)</Label>
                  <Input
                    id="cc-extra"
                    data-testid="send-confirm-cc-input"
                    placeholder="legal@example.com, advisor@example.com"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    disabled={busy}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Comma-separated. Useful for legal advisors or co-signatories.
                  </p>
                </div>
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-600">
                  Once sent, the contract becomes locked and cannot be edited.
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => !busy && setShowSendConfirm(false)} disabled={busy}>
                  <X className="h-4 w-4 mr-1.5" /> Cancel
                </Button>
                <Button
                  data-testid="send-confirm-submit-btn"
                  onClick={performSend}
                  disabled={busy}
                  className="bg-gold text-black hover:bg-gold/90"
                >
                  {busy
                    ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <Send className="h-4 w-4 mr-1.5" />}
                  Send Contract
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// Template-specific field components
// ────────────────────────────────────────────────────────────
function MediaPartnershipFields({ v, setVar, options, disabled }) {
  const scopeOptions = options?.scope_of_work || [];
  // Iter 69.1.1 — append "Custom" as the 5th option
  const durations = [...(options?.duration_options || []), 'Custom'];
  const selected = v.scope_of_work || [];
  const isCustomDuration = (v.duration || '').toLowerCase() === 'custom';

  const toggleScope = (item) => {
    const next = selected.includes(item)
      ? selected.filter(s => s !== item)
      : [...selected, item];
    setVar('scope_of_work', next);
  };

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Client Contact Name</Label>
          <Input data-testid="field-contact-name" disabled={disabled}
                 value={v.contact_name || ''}
                 onChange={e => setVar('contact_name', e.target.value)} />
        </div>
        <div>
          <Label>Date of Agreement</Label>
          <ContractDatePicker
            testId="field-agreement-date"
            disabled={disabled}
            value={v.agreement_date || ''}
            onChange={(iso) => setVar('agreement_date', iso)}
          />
        </div>
      </div>

      <div>
        <Label>Scope of Work (select all that apply)</Label>
        <div className="grid grid-cols-2 gap-1.5 mt-1.5 p-3 border border-border rounded-md">
          {scopeOptions.map(item => (
            <label
              key={item}
              data-testid={`scope-checkbox-${item.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`}
              className={`flex items-center gap-2 text-sm cursor-pointer hover:text-gold transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <Checkbox
                checked={selected.includes(item)}
                onCheckedChange={() => !disabled && toggleScope(item)}
                disabled={disabled}
              />
              <span>{item}</span>
            </label>
          ))}
        </div>
        <Input
          data-testid="field-scope-other"
          disabled={disabled}
          placeholder="Other (custom service)"
          className="mt-2"
          value={v.scope_other || ''}
          onChange={e => setVar('scope_other', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Contract Duration</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {durations.map(d => (
              <button
                key={d}
                type="button"
                disabled={disabled}
                data-testid={`duration-${d.replace(/\s+/g, '-')}`}
                onClick={() => !disabled && setVar('duration', d)}
                className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  v.duration === d
                    ? 'border-gold bg-gold/10 text-gold'
                    : 'border-border hover:border-gold/60'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {d}
              </button>
            ))}
          </div>
          {isCustomDuration && (
            <Input
              data-testid="field-duration-custom"
              disabled={disabled}
              placeholder="e.g. 2 months, 4 weeks, 18 months"
              className="mt-2"
              value={v.duration_custom || ''}
              onChange={e => setVar('duration_custom', e.target.value)}
            />
          )}
        </div>
        <div>
          <Label>Total Price (numeric, "+ taxes" appended)</Label>
          <Input data-testid="field-price" type="number" disabled={disabled}
                 value={v.price || ''}
                 onChange={e => setVar('price', e.target.value)} />
        </div>
        <div>
          <Label>Start Date</Label>
          <ContractDatePicker
            testId="field-start-date"
            disabled={disabled}
            value={v.start_date || ''}
            onChange={(iso) => setVar('start_date', iso)}
          />
        </div>
        <div>
          <Label>End Date (optional, "NA" if open-ended)</Label>
          <Input data-testid="field-end-date" disabled={disabled}
                 value={v.end_date || ''}
                 onChange={e => setVar('end_date', e.target.value)}
                 placeholder="YYYY-MM-DD or NA" />
        </div>
        <div className="col-span-2">
          <Label>Ad Budget Allocation</Label>
          <Input data-testid="field-ad-budget" disabled={disabled}
                 value={v.ad_budget ?? '$200'}
                 onChange={e => setVar('ad_budget', e.target.value)} />
        </div>
      </div>

      <div>
        <Label>Deliverables Summary</Label>
        <Textarea
          data-testid="field-deliverables"
          disabled={disabled}
          rows={4}
          placeholder={
            options?.default_deliverables
              ? `• Videos/Reels per month: ${options.default_deliverables.videos_per_month}\n• Posts per month: ${options.default_deliverables.posts_per_month}\n• Ad budget per month: ${options.default_deliverables.ad_budget_per_month}`
              : 'Bulleted summary of monthly deliverables'
          }
          value={v.deliverables || ''}
          onChange={e => setVar('deliverables', e.target.value)}
        />
      </div>

      {/* Iter 69.2 — Advanced Terms (collapsed by default) */}
      <details className="border border-border rounded-md" data-testid="advanced-terms-section">
        <summary className="px-3 py-2 cursor-pointer text-sm font-medium hover:bg-muted/30">
          Advanced Terms
        </summary>
        <div className="p-3 grid grid-cols-2 gap-3">
          <div>
            <Label>Governing Jurisdiction</Label>
            <Input data-testid="field-governing-jurisdiction" disabled={disabled}
                   placeholder="Saskatchewan, Canada"
                   value={v.governing_jurisdiction ?? ''}
                   onChange={e => setVar('governing_jurisdiction', e.target.value)} />
          </div>
          <div>
            <Label>Late Fee Percentage</Label>
            <Input data-testid="field-late-fee-pct" disabled={disabled} type="number"
                   placeholder="5"
                   value={v.late_fee_pct ?? ''}
                   onChange={e => setVar('late_fee_pct', e.target.value)} />
          </div>
          <div>
            <Label>Grace Period (days)</Label>
            <Input data-testid="field-grace-days" disabled={disabled} type="number"
                   placeholder="5"
                   value={v.grace_days ?? ''}
                   onChange={e => setVar('grace_days', e.target.value)} />
          </div>
          <div>
            <Label>Termination Notice (days)</Label>
            <Input data-testid="field-termination-notice-days" disabled={disabled} type="number"
                   placeholder="15"
                   value={v.termination_notice_days ?? ''}
                   onChange={e => setVar('termination_notice_days', e.target.value)} />
          </div>
          <div>
            <Label>Non-Solicitation (years)</Label>
            <Input data-testid="field-non-solicit-years" disabled={disabled} type="number"
                   placeholder="2"
                   value={v.non_solicit_years ?? ''}
                   onChange={e => setVar('non_solicit_years', e.target.value)} />
          </div>
          <div>
            <Label>Feedback Window (business days)</Label>
            <Input data-testid="field-feedback-days" disabled={disabled} type="number"
                   placeholder="3"
                   value={v.feedback_days ?? ''}
                   onChange={e => setVar('feedback_days', e.target.value)} />
          </div>
        </div>
      </details>
    </section>
  );
}

function OfferLetterFields({ v, setVar, options, disabled }) {
  const offices = options?.office_locations || ['Ahmedabad', 'Regina'];
  const currencies = options?.currency_options || ['INR', 'CAD'];

  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Position Title</Label>
          <Input data-testid="field-position-title" disabled={disabled}
                 value={v.position_title || ''}
                 onChange={e => setVar('position_title', e.target.value)} />
        </div>
        <div>
          <Label>Start Date</Label>
          <ContractDatePicker
            testId="field-start-date"
            disabled={disabled}
            value={v.start_date || ''}
            onChange={(iso) => setVar('start_date', iso)}
          />
        </div>
        <div>
          <Label>Office Location</Label>
          <div className="flex gap-2 mt-1.5">
            {offices.map(o => (
              <button key={o} type="button" disabled={disabled}
                data-testid={`office-${o}`}
                onClick={() => !disabled && setVar('office_location', o)}
                className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  v.office_location === o ? 'border-gold bg-gold/10 text-gold' : 'border-border hover:border-gold/60'
                } ${disabled ? 'opacity-60' : ''}`}
              >{o}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Letter Date</Label>
          <ContractDatePicker
            testId="field-letter-date"
            disabled={disabled}
            value={v.letter_date || ''}
            onChange={(iso) => setVar('letter_date', iso)}
          />
        </div>
        <div>
          <Label>Currency</Label>
          <div className="flex gap-2 mt-1.5">
            {currencies.map(c => (
              <button key={c} type="button" disabled={disabled}
                data-testid={`currency-${c}`}
                onClick={() => !disabled && setVar('currency', c)}
                className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  (v.currency || 'INR') === c ? 'border-gold bg-gold/10 text-gold' : 'border-border hover:border-gold/60'
                } ${disabled ? 'opacity-60' : ''}`}
              >{c === 'INR' ? '₹ INR' : '$ CAD'}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Monthly Salary</Label>
          <Input data-testid="field-monthly-salary" type="number" disabled={disabled}
                 value={v.monthly_salary || ''}
                 onChange={e => setVar('monthly_salary', e.target.value)} />
        </div>
        <div>
          <Label>Probationary Period (months)</Label>
          <Input data-testid="field-probation-months" type="number" disabled={disabled}
                 value={v.probationary_months ?? 4}
                 onChange={e => setVar('probationary_months', e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Responsibilities (editable)</Label>
        <Textarea
          data-testid="field-responsibilities"
          disabled={disabled}
          rows={6}
          placeholder={options?.default_responsibilities || ''}
          value={v.responsibilities ?? (options?.default_responsibilities || '')}
          onChange={e => setVar('responsibilities', e.target.value)}
        />
      </div>
    </section>
  );
}

function EmploymentAgreementFields({ v, setVar, options, disabled }) {
  const offices = options?.office_locations || ['Ahmedabad', 'Regina'];
  const currencies = options?.currency_options || ['INR', 'CAD'];

  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Position Title</Label>
          <Input data-testid="field-position-title" disabled={disabled}
                 value={v.position_title || ''}
                 onChange={e => setVar('position_title', e.target.value)} />
        </div>
        <div>
          <Label>Start Date</Label>
          <ContractDatePicker
            testId="field-start-date"
            disabled={disabled}
            value={v.start_date || ''}
            onChange={(iso) => setVar('start_date', iso)}
          />
        </div>
        <div>
          <Label>Office Location</Label>
          <div className="flex gap-2 mt-1.5">
            {offices.map(o => (
              <button key={o} type="button" disabled={disabled}
                data-testid={`office-${o}`}
                onClick={() => !disabled && setVar('office_location', o)}
                className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  v.office_location === o ? 'border-gold bg-gold/10 text-gold' : 'border-border hover:border-gold/60'
                } ${disabled ? 'opacity-60' : ''}`}
              >{o}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Date of Agreement</Label>
          <ContractDatePicker
            testId="field-agreement-date"
            disabled={disabled}
            value={v.agreement_date || ''}
            onChange={(iso) => setVar('agreement_date', iso)}
          />
        </div>
        <div>
          <Label>Currency</Label>
          <div className="flex gap-2 mt-1.5">
            {currencies.map(c => (
              <button key={c} type="button" disabled={disabled}
                data-testid={`currency-${c}`}
                onClick={() => !disabled && setVar('currency', c)}
                className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  (v.currency || 'INR') === c ? 'border-gold bg-gold/10 text-gold' : 'border-border hover:border-gold/60'
                } ${disabled ? 'opacity-60' : ''}`}
              >{c === 'INR' ? '₹ INR' : '$ CAD'}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Monthly Gross Salary (post-probation)</Label>
          <Input data-testid="field-monthly-gross-salary" type="number" disabled={disabled}
                 value={v.monthly_gross_salary || ''}
                 onChange={e => setVar('monthly_gross_salary', e.target.value)} />
        </div>
        <div>
          <Label>Probationary Salary</Label>
          <Input data-testid="field-probationary-salary" type="number" disabled={disabled}
                 value={v.probationary_salary || ''}
                 onChange={e => setVar('probationary_salary', e.target.value)} />
        </div>
        <div>
          <Label>Probationary Period (months)</Label>
          <Input data-testid="field-probation-months" type="number" disabled={disabled}
                 value={v.probationary_months ?? 4}
                 onChange={e => setVar('probationary_months', e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label>Working Hours</Label>
          <Input data-testid="field-working-hours" disabled={disabled}
                 value={v.working_hours ?? 'Monday to Friday, 9:00 AM to 5:00 PM'}
                 onChange={e => setVar('working_hours', e.target.value)} />
        </div>
        <div>
          <Label>Sick Leave per Month</Label>
          <Input data-testid="field-sick-leave" disabled={disabled}
                 value={v.sick_leave_per_month ?? '2 days'}
                 onChange={e => setVar('sick_leave_per_month', e.target.value)} />
        </div>
        <div>
          <Label>Notice Period</Label>
          <Input data-testid="field-notice-period" disabled={disabled}
                 value={v.notice_period ?? '1 month'}
                 onChange={e => setVar('notice_period', e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label>Governing Jurisdiction (city / state)</Label>
          <Input data-testid="field-jurisdiction" disabled={disabled}
                 value={v.governing_jurisdiction ?? ''}
                 placeholder={(v.office_location === 'Ahmedabad') ? 'Ahmedabad, Gujarat' : 'Regina, Saskatchewan'}
                 onChange={e => setVar('governing_jurisdiction', e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label>Governing Law Country</Label>
          <Input data-testid="field-governing-law-country" disabled={disabled}
                 value={v.governing_law_country ?? ''}
                 placeholder={(v.office_location === 'Ahmedabad') ? 'laws of India' : 'laws of Saskatchewan, Canada'}
                 onChange={e => setVar('governing_law_country', e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Duties (editable, role-specific)</Label>
        <Textarea
          data-testid="field-duties"
          disabled={disabled}
          rows={6}
          placeholder={options?.default_duties || ''}
          value={v.duties ?? (options?.default_duties || '')}
          onChange={e => setVar('duties', e.target.value)}
        />
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Signed PDF upload sub-modal
// ────────────────────────────────────────────────────────────
function SignedPdfUploadModal({ wsId, contractId, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) {
      toast.error('Pick a signed PDF first');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File exceeds 50MB limit');
      return;
    }
    setUploading(true);
    try {
      await contractsAPI.markSigned(wsId, contractId, file);
      toast.success('Marked as signed');
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="signed-upload-modal">
        <DialogHeader>
          <DialogTitle>Upload signed PDF</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Upload the PDF you received back from the recipient. Max 50MB.
          </p>
          <Input
            data-testid="signed-file-input"
            type="file" accept="application/pdf"
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
          {file && (
            <p className="text-xs text-muted-foreground">
              Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={uploading}>
            <X className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <Button
            data-testid="confirm-mark-signed-btn"
            onClick={submit} disabled={uploading || !file}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {uploading
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <Upload className="h-4 w-4 mr-1.5" />}
            Upload &amp; Mark Signed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Iter 69.3 — Form components for HR exit templates
// ─────────────────────────────────────────────────────────────────────────

function OfficeLocationSelect({ v, setVar, disabled }) {
  return (
    <div>
      <Label>Office Location</Label>
      <select
        data-testid="field-office-location"
        disabled={disabled}
        value={v.office_location || 'Regina'}
        onChange={e => setVar('office_location', e.target.value)}
        className="w-full bg-background border border-input rounded px-3 py-2 text-sm"
      >
        <option value="Regina">Regina (Canada)</option>
        <option value="Ahmedabad">Ahmedabad (India)</option>
      </select>
    </div>
  );
}

function ResignationAcceptanceFields({ v, setVar, disabled }) {
  return (
    <section className="grid grid-cols-2 gap-3" data-testid="resignation-form">
      <div className="col-span-2">
        <Label>Position Title</Label>
        <Input data-testid="field-position-title" disabled={disabled}
               value={v.position_title || ''}
               onChange={e => setVar('position_title', e.target.value)} />
      </div>
      <OfficeLocationSelect v={v} setVar={setVar} disabled={disabled} />
      <div>
        <Label>Letter Date</Label>
        <ContractDatePicker testId="field-letter-date" disabled={disabled}
                             value={v.letter_date || ''}
                             onChange={iso => setVar('letter_date', iso)} />
      </div>
      <div>
        <Label>Resignation Date</Label>
        <ContractDatePicker testId="field-resignation-date" disabled={disabled}
                             value={v.resignation_date || ''}
                             onChange={iso => setVar('resignation_date', iso)} />
      </div>
      <div>
        <Label>Last Working Day</Label>
        <ContractDatePicker testId="field-last-working-day" disabled={disabled}
                             value={v.last_working_day || ''}
                             onChange={iso => setVar('last_working_day', iso)} />
      </div>
      <div className="col-span-2">
        <Label>Knowledge Transfer Period</Label>
        <Input data-testid="field-knowledge-transfer" disabled={disabled}
               placeholder="the remainder of your notice period"
               value={v.knowledge_transfer_period || ''}
               onChange={e => setVar('knowledge_transfer_period', e.target.value)} />
      </div>
      <div className="col-span-2">
        <Label>Final Settlement Window</Label>
        <Input data-testid="field-final-settlement" disabled={disabled}
               placeholder="within 30 days of your last working day"
               value={v.final_settlement_date || ''}
               onChange={e => setVar('final_settlement_date', e.target.value)} />
      </div>
    </section>
  );
}

function ExperienceLetterFields({ v, setVar, disabled }) {
  return (
    <section className="grid grid-cols-2 gap-3" data-testid="experience-form">
      <div className="col-span-2">
        <Label>Position Title (last held)</Label>
        <Input data-testid="field-position-title" disabled={disabled}
               value={v.position_title || ''}
               onChange={e => setVar('position_title', e.target.value)} />
      </div>
      <OfficeLocationSelect v={v} setVar={setVar} disabled={disabled} />
      <div>
        <Label>Letter Date</Label>
        <ContractDatePicker testId="field-letter-date" disabled={disabled}
                             value={v.letter_date || ''}
                             onChange={iso => setVar('letter_date', iso)} />
      </div>
      <div>
        <Label>Employment Start Date</Label>
        <ContractDatePicker testId="field-employment-start" disabled={disabled}
                             value={v.employment_start_date || ''}
                             onChange={iso => setVar('employment_start_date', iso)} />
      </div>
      <div>
        <Label>Employment End Date <span className="text-[10px] text-muted-foreground">(or "current")</span></Label>
        <ContractDatePicker testId="field-employment-end" disabled={disabled}
                             value={v.employment_end_date || ''}
                             onChange={iso => setVar('employment_end_date', iso)} />
      </div>
      <div>
        <Label>Pronouns</Label>
        <select
          data-testid="field-pronouns"
          disabled={disabled}
          value={v.pronouns || 'they/them/their'}
          onChange={e => setVar('pronouns', e.target.value)}
          className="w-full bg-background border border-input rounded px-3 py-2 text-sm"
        >
          <option value="he/him/his">he / him / his</option>
          <option value="she/her/her">she / her / her</option>
          <option value="they/them/their">they / them / their</option>
        </select>
      </div>
      <div>
        <Label>Issue Reason</Label>
        <select
          data-testid="field-issue-reason"
          disabled={disabled}
          value={v.issue_reason || 'On Request — Current Employee'}
          onChange={e => setVar('issue_reason', e.target.value)}
          className="w-full bg-background border border-input rounded px-3 py-2 text-sm"
        >
          <option value="On Resignation">On Resignation</option>
          <option value="On Termination">On Termination</option>
          <option value="On Request — Current Employee">On Request — Current Employee</option>
        </select>
      </div>
    </section>
  );
}

function TerminationLetterFields({ v, setVar, disabled }) {
  const withCause = v.termination_type === 'With Cause';
  return (
    <section className="grid grid-cols-2 gap-3" data-testid="termination-form">
      <div className="col-span-2">
        <Label>Position Title</Label>
        <Input data-testid="field-position-title" disabled={disabled}
               value={v.position_title || ''}
               onChange={e => setVar('position_title', e.target.value)} />
      </div>
      <OfficeLocationSelect v={v} setVar={setVar} disabled={disabled} />
      <div>
        <Label>Termination Type</Label>
        <select
          data-testid="field-termination-type"
          disabled={disabled}
          value={v.termination_type || 'Without Cause'}
          onChange={e => setVar('termination_type', e.target.value)}
          className="w-full bg-background border border-input rounded px-3 py-2 text-sm"
        >
          <option value="Without Cause">Without Cause</option>
          <option value="With Cause">With Cause</option>
          <option value="End of Probation">End of Probation</option>
          <option value="Redundancy / Position Eliminated">Redundancy / Position Eliminated</option>
        </select>
      </div>
      <div>
        <Label>Letter Date</Label>
        <ContractDatePicker testId="field-letter-date" disabled={disabled}
                             value={v.letter_date || ''}
                             onChange={iso => setVar('letter_date', iso)} />
      </div>
      <div>
        <Label>Last Working Day</Label>
        <ContractDatePicker testId="field-last-working-day" disabled={disabled}
                             value={v.last_working_day || ''}
                             onChange={iso => setVar('last_working_day', iso)} />
      </div>
      <div className="col-span-2">
        <Label>Notice Period Given (text)</Label>
        <Input data-testid="field-notice-period" disabled={disabled}
               placeholder="You have been given notice as per your Employment Agreement."
               value={v.notice_period_given_text || ''}
               onChange={e => setVar('notice_period_given_text', e.target.value)} />
      </div>
      <div className="col-span-2">
        <Label>Severance Amount <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
        <Input data-testid="field-severance-amount" disabled={disabled}
               placeholder="e.g. CAD $5,000 or 2 months gross"
               value={v.severance_amount || ''}
               onChange={e => setVar('severance_amount', e.target.value)} />
      </div>
      {withCause && (
        <div className="col-span-2">
          <Label>Reason Details <span className="text-[10px] text-amber-500">(required for With Cause)</span></Label>
          <Textarea data-testid="field-reason-details" disabled={disabled} rows={4}
                    placeholder="Describe the cause: repeated misconduct, written warnings issued on X/Y, etc."
                    value={v.reason_details || ''}
                    onChange={e => setVar('reason_details', e.target.value)} />
        </div>
      )}
    </section>
  );
}

// =====================================================================
// D71 — Contract Engagement / Tracking section (sent/signed contracts only)
// =====================================================================
function ContractTrackingSection({ tracking, events, isOwner, onResend, busy }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState('');

  const EVENT_ICONS = {
    sent:           { label: 'Sent',          color: 'text-slate-300' },
    delivered:      { label: 'Delivered',     color: 'text-slate-300' },
    bounced_failed: { label: 'Bounced',       color: 'text-red-400'   },
    opened:         { label: 'Opened',        color: 'text-sky-400'   },
    viewed:         { label: 'Viewed',        color: 'text-emerald-400' },
    reminder_sent:  { label: 'Reminder sent', color: 'text-amber-400' },
  };

  return (
    <section
      className="border-t border-border pt-4 space-y-3"
      data-testid="contract-tracking-section"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-medium flex items-center gap-2">
          Email tracking
          <TrackingBadge tracking={tracking} />
          <FollowUpFlag tracking={tracking} />
        </h3>
        {isOwner && (
          <Button
            data-testid="resend-reminder-btn"
            size="sm"
            variant={tracking.needs_followup ? 'default' : 'outline'}
            className={tracking.needs_followup
              ? 'bg-amber-500 text-black hover:bg-amber-500/90'
              : ''}
            disabled={busy}
            onClick={() => setConfirmOpen(true)}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Resend Reminder
          </Button>
        )}
      </div>

      {tracking.needs_followup && (
        <div
          data-testid="needs-followup-banner"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
        >
          {tracking.followup_reason === 'bounced'
            ? 'Email bounced or was rejected. Verify the recipient address and click Resend Reminder to try again.'
            : 'Recipient hasn\'t opened or viewed this in 3+ days. Click Resend Reminder for a one-time follow-up.'}
        </div>
      )}

      {/* Compact event timeline */}
      <ol
        className="border border-border rounded-md divide-y divide-border"
        data-testid="email-events-timeline"
      >
        {events.length === 0 ? (
          <li className="px-3 py-2 text-xs text-muted-foreground">
            No tracking events yet.
          </li>
        ) : (
          [...events].reverse().map((ev) => {
            const meta = EVENT_ICONS[ev.event_type] || { label: ev.event_type, color: '' };
            return (
              <li
                key={ev.id || `${ev.event_type}-${ev.timestamp}`}
                className="px-3 py-2 text-xs flex items-center justify-between gap-2"
                data-testid={`email-event-${ev.event_type}`}
              >
                <span className={`font-medium ${meta.color}`}>{meta.label}</span>
                <span className="text-muted-foreground">{fmtDateTime(ev.timestamp)}</span>
              </li>
            );
          })
        )}
      </ol>

      {tracking.resend_count > 0 && (
        <div className="text-[11px] text-muted-foreground">
          {tracking.resend_count} reminder{tracking.resend_count === 1 ? '' : 's'} sent
          {tracking.last_resend_at && ` · last: ${fmtDateTime(tracking.last_resend_at)}`}
        </div>
      )}

      {/* Confirmation modal — reminder NEVER auto-sends; only on click. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="resend-confirm-modal">
          <DialogHeader>
            <DialogTitle>Send reminder?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Resends the same contract PDF with a friendly reminder lede.
              Goes from <span className="font-medium text-gold">dhrumil@stellarproductions.ca</span>.
              You can add an optional personal note below.
            </p>
            <div>
              <Label>Optional note</Label>
              <Textarea
                data-testid="resend-note-input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Just bumping this up in case you missed it…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="resend-confirm-cancel"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              data-testid="resend-confirm-send"
              className="bg-gold text-black hover:bg-gold/90"
              disabled={busy}
              onClick={async () => {
                setConfirmOpen(false);
                await onResend(note);
                setNote('');
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Send Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


